import _ from 'lodash';
import React from 'react';
import {connect} from 'react-redux';
import {reduxForm, Field, change, formValueSelector} from 'redux-form';

import {validDate} from 'utils/validators';

import {CREDIT_CARD_MACHINE} from 'ducks/tenancy';
import {createOneOffChargeInvoiceAndPayment} from 'ducks/bookings';

import Label from 'components/Label';
import FormField from 'components/FormField';
import ErrorBlock from 'components/ErrorBlock';
import {
  SelectField,
  TextField,
  MoneyField,
  CheckBoxField,
  DateField,
  NumberField
} from 'components/Fields';

import {
  prepareDataForPost,
  OTHER_LABEL
} from 'components/CommonModals/components/OneOffChargeModal/components/AddInvoiceItemAndPaymentForm/utils';

import {formatDate, getToday, getDate} from 'utils/datetime';

import './styles.css';

const formSubmit = (values, dispatch, props) => {
  const {
    oneOffChargeReason,
    description,
    amount,
    paymentMethod,
    paymentDate,
    invoiceDueDate,
    reference,
    batchNumber,
    creditCardNumber,
    createPayment
  } = values;

  const {bookingId, extraActions} = props;

  const data = prepareDataForPost({
    oneOffChargeReason,
    description,
    amount,
    paymentMethod,
    paymentDate,
    invoiceDueDate,
    reference,
    batchNumber,
    creditCardNumber,
    createPayment,
    bookingId
  });

  dispatch(createOneOffChargeInvoiceAndPayment(data, extraActions || []));
};

const STEP_1 = 1;
const STEP_2 = 2;
const STEP_3 = 3;
const STEP_4 = 4;

class AddInvoiceItemAndPaymentForm extends React.Component<any> {
  /*
  Current Form has 4 steps of revealing the fields:

  Step 1: Choose item type
    Available Fields: `oneOffChargeReason`

  Step 2: Fill item  details and `paymentMethod`
    Available Fields: `oneOffChargeReason`,  `description`, `amount`, `paymentMethod`

  Step 3: Fill payment date (only if Step 2 has been done)
    Available Fields: `oneOffChargeReason`,  `description`, `amount`, `paymentMethod`, `paymentDate`

  Step 4: Fill payment reference (only if Step 3 has been done)
    Available Fields: `oneOffChargeReason`,  `description`, `amount`, `paymentMethod`, `paymentDate`, `reference` (all fields)
 */
  state = {
    currentStep: STEP_1
  };

  onItemTypeChange = value => {
    const {description, amount} = value;

    this.props.dispatch(
      change('createOneOffChargeInvoiceAndPayment', 'description', description)
    );
    this.props.dispatch(
      change('createOneOffChargeInvoiceAndPayment', 'amount', amount)
    );
  };

  step1IsCompleted = () => !_.isNil(this.props.oneOffChargeReasonFormValue);

  step2IsCompleted = () =>
    this.step1IsCompleted() && !_.isNil(this.props.paymentMethodFormValue);

  step3IsCompleted = () =>
    this.step2IsCompleted() && !_.isNil(this.props.paymentDateFormValue);

  defineFormStep = () => {
    const {currentStep} = this.state;

    if (this.step1IsCompleted() && currentStep === STEP_1)
      this.setState({currentStep: STEP_2});

    if (this.step2IsCompleted() && currentStep < STEP_3)
      this.setState({currentStep: STEP_3});

    if (this.step3IsCompleted() && currentStep < STEP_4)
      this.setState({currentStep: STEP_4});
  };

  onPaymentMethodChange = option => {
    if (_.get(option, 'value') != CREDIT_CARD_MACHINE) {
      this.props.dispatch(
        change('createOneOffChargeInvoiceAndPayment', 'batchNumber', null)
      );
      this.props.dispatch(
        change('createOneOffChargeInvoiceAndPayment', 'creditCardNumber', null)
      );
    }
  };

  resetPaymentFields = () => {
    this.props.change('paymentMethod', null);
    this.props.change('batchNumber', null);
    this.props.change('creditCardNumber', null);
    this.props.change('paymentDate', null);
    this.props.change('reference', null);
  };

  onDueDateChange = date => {
    if (formatDate(getDate(getToday())) !== date) {
      this.props.change('createPayment', false);

      this.resetPaymentFields();
    }
  };

  onCreatePaymentChange = paymentReceived => {
    if (!paymentReceived) {
      this.resetPaymentFields();
    }
  };

  render() {
    const {
      error,
      handleSubmit,
      paymentMethods,
      paymentMethodFormValue,
      invoiceDueDate,
      createPaymentValue,
      oneOffChargeReasonOptions
    } = this.props;
    const {currentStep} = this.state;

    this.defineFormStep();

    const today = getDate(getToday());
    const canPaymentBeReceived = formatDate(today) == invoiceDueDate;
    const chargeMachineSelected =
      _.get(paymentMethodFormValue, 'value') === CREDIT_CARD_MACHINE;

    return (
      <form onSubmit={handleSubmit} className="AddInvoiceItemAndPaymentForm">
        {error && <ErrorBlock>{error}</ErrorBlock>}

        <FormField>
          <Label>Select item type</Label>
          <Field
            name="oneOffChargeReason"
            component={SelectField}
            options={oneOffChargeReasonOptions}
            placeholder="Choose an item..."
            onChange={this.onItemTypeChange}
          />
        </FormField>

        {currentStep >= STEP_2 && (
          <React.Fragment>
            <FormField>
              <Label>Description</Label>
              <Field
                name="description"
                component={TextField}
                placeholder="Please describe..."
                disabled={!this.step1IsCompleted()}
              />
            </FormField>

            <FormField>
              <Label>Confirm Amount</Label>
              <Field
                name="amount"
                component={MoneyField}
                disabled={!this.step1IsCompleted()}
                placeholder="Confirm amount..."
              />
            </FormField>

            <FormField>
              <Label>Invoice Due Date</Label>
              <Field
                name="invoiceDueDate"
                component={DateField}
                validate={[validDate]}
                placeholder="Chose a date..."
                dayPickerProps={{disabledDays: {before: today}}}
                onDateChange={this.onDueDateChange}
              />
            </FormField>

            {canPaymentBeReceived && (
              <React.Fragment>
                <div className="formDivider" />

                <h3 className="paymentHeader">Payment</h3>

                <FormField>
                  <Field
                    name="createPayment"
                    component={CheckBoxField}
                    label="Payment received"
                    disabled={!canPaymentBeReceived}
                    onCheckBoxChange={this.onCreatePaymentChange}
                  />
                </FormField>

                <FormField>
                  <Label>Payment method</Label>
                  <Field
                    name="paymentMethod"
                    component={SelectField}
                    placeholder="Choose a method..."
                    options={paymentMethods}
                    onChange={this.onPaymentMethodChange}
                    disabled={!createPaymentValue}
                  />
                </FormField>

                {chargeMachineSelected && (
                  <React.Fragment>
                    <FormField>
                      <Label>Batch Number</Label>
                      <Field name="batchNumber" component={NumberField} />
                    </FormField>

                    <FormField>
                      <Label>Credit card number(last 4 digits)</Label>
                      <Field name="creditCardNumber" component={NumberField} />
                    </FormField>
                  </React.Fragment>
                )}

                {currentStep >= STEP_3 && (
                  <React.Fragment>
                    <FormField>
                      <Label>Payment Date</Label>
                      <Field
                        name="paymentDate"
                        component={DateField}
                        validate={[validDate]}
                        placeholder="Chose a date..."
                        disabled={!this.step2IsCompleted()}
                      />
                    </FormField>

                    {currentStep >= STEP_4 && (
                      <FormField>
                        <Label>Reference</Label>
                        <Field
                          name="reference"
                          component={TextField}
                          placeholder="Optional..."
                          disabled={!this.step3IsCompleted()}
                        />
                      </FormField>
                    )}
                  </React.Fragment>
                )}
              </React.Fragment>
            )}
          </React.Fragment>
        )}
      </form>
    );
  }
}

const formSelector = formValueSelector('createOneOffChargeInvoiceAndPayment');

const mapStateToProps = (state, props) => {
  const oneOffChargeReasonOptions = [
    ...props.oneOffChargeReasons,
    {label: OTHER_LABEL}
  ];

  return {
    amountFormValue: formSelector(state, 'amount'),
    oneOffChargeReasonFormValue: formSelector(state, 'oneOffChargeReason'),
    paymentDateFormValue: formSelector(state, 'paymentDate'),
    paymentMethodFormValue: formSelector(state, 'paymentMethod'),
    invoiceDueDate: formSelector(state, 'invoiceDueDate'),
    createPaymentValue: formSelector(state, 'createPayment'),
    oneOffChargeReasonOptions,
    initialValues: {
      createPayment: false,
      invoiceDueDate: getToday(),
      oneOffChargeReason: _.get(oneOffChargeReasonOptions, '[0]')
    }
  };
};

export default connect(mapStateToProps)(
  reduxForm({
    form: 'createOneOffChargeInvoiceAndPayment',
    onSubmit: formSubmit,
    enableReinitialize: true
  })(AddInvoiceItemAndPaymentForm)
) as React.ComponentType<any>;
